// TransferToAgentModal.tsx — "This lead is really an agent": confirm, then the
// lead becomes an agent record and is removed from Leads. Admin-only (the
// button is hidden for staff and the API refuses them).

import React, { useState } from 'react';
import { UserCheck, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog, { Notice } from '../../Proposals/parts/ProposalDialog';
import { apiError } from '../../Proposals/parts/proposalTypes';
import { Agent, agentApi } from '../../Agents/parts/agentTypes';

interface TransferToAgentModalProps {
  visible: boolean;
  leadId: string;
  leadName: string;
  onHide: () => void;
  /** the new agent — the lead no longer exists, so leave the lead page */
  onTransferred: (agent: Agent) => void;
}

const TransferToAgentModal: React.FC<TransferToAgentModalProps> = ({ visible, leadId, leadName, onHide, onTransferred }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (busy) return;
    setError(null);
    onHide();
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await agentApi.transferLead(leadId);
      onTransferred(res.data);
    } catch (e) {
      setError(apiError(e, 'Could not move this lead to Agents. Nothing was changed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProposalDialog
      visible={visible}
      onHide={close}
      title="Move to Agents?"
      subtitle={<strong style={{ color: C.teal }}>{leadName}</strong>}
      icon={UserCheck}
      busy={busy}
      maxWidth="460px"
      footer={
        <>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={close} disabled={busy} />
          <GlobalButton label={busy ? 'MOVING…' : 'MOVE TO AGENTS'} icon={UserCheck} onClick={confirm} loading={busy} />
        </>
      }
    >
      {error && <Notice tone="error">{error}</Notice>}
      <p style={{ margin: 0, fontSize: '0.78rem', color: C.sub, lineHeight: 1.6 }}>
        Their name, phone, email and city become an agent record, and this lead leaves the Leads list. <strong>Nothing is deleted</strong> — the full history (status, notes, calls, proposals) is kept, and you can move them back to Leads from the Agents page. Syncing the Google Sheet again will not bring the row back.
      </p>
    </ProposalDialog>
  );
};

export default TransferToAgentModal;